import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { paymentApi, couponApi } from '../../api/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Shield,
  Check,
  Star,
  Loader2,
  HardDrive,
  Mail,
  FolderPlus,
  Clock,
  AlertTriangle,
  Ticket,
  X,
  AlertCircle
} from 'lucide-react';

export default function PaymentRequiredModal({ isOpen, trialDaysLeft, onClose }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const activePlans = await paymentApi.getActivePlans();
        setPlans(activePlans);
        // Auto-select the featured plan
        const featured = activePlans.find(p => p.is_featured);
        if (featured) {
          setSelectedPlan(featured);
        } else if (activePlans.length > 0) {
          setSelectedPlan(activePlans[0]);
        }
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen]);

  const formatPrice = (plan) => {
    const price = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
    return `$${price}`;
  };

  // Validate coupon
  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponValidating(true);
    setCouponError('');
    setAppliedCoupon(null);
    
    try {
      const planPrice = selectedPlan ? 
        (billingCycle === 'yearly' ? selectedPlan.yearly_price : selectedPlan.monthly_price) : null;
      
      const result = await couponApi.validateCoupon(
        couponCode.trim(),
        selectedPlan?.id,
        planPrice
      );
      
      if (result.valid) {
        setAppliedCoupon(result);
      }
    } catch (error) {
      setCouponError(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setCouponValidating(false);
    }
  };
  
  const clearCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponError('');
  };

  const handleCheckout = async () => {
    if (!selectedPlan) return;
    
    setCheckoutLoading(true);
    try {
      const result = await paymentApi.createCheckout(
        selectedPlan.id, 
        billingCycle, 
        appliedCoupon?.code || null
      );
      window.location.href = result.url;
    } catch (error) {
      console.error('Checkout error:', error);
      const detail = error.response?.data?.detail || 'Failed to start checkout. Please try again.';
      toast.error(detail);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isTrialExpired = trialDaysLeft !== undefined && trialDaysLeft <= 0;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="bg-[#16162a] border-[#2d2d44] max-w-3xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center pb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] rounded-2xl flex items-center justify-center mx-auto mb-4">
            {isTrialExpired ? (
              <AlertTriangle className="w-8 h-8 text-white" />
            ) : (
              <Shield className="w-8 h-8 text-white" />
            )}
          </div>
          <DialogTitle className="text-2xl font-bold text-white">
            {isTrialExpired ? 'Trial Expired' : 'Upgrade Your Account'}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {isTrialExpired ? (
              'Your 7-day free trial has ended. Select a plan to continue using MyPatriotMail.'
            ) : (
              <>
                <span className="flex items-center justify-center gap-2 text-yellow-500">
                  <Clock className="w-4 h-4" />
                  {trialDaysLeft} days left in your trial
                </span>
                <span className="block mt-1">Upgrade now to keep your encrypted inbox</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center bg-[#1a1a2e] border border-[#2d2d44] rounded-full p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-[#6D4AFF] text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'yearly' 
                  ? 'bg-[#6D4AFF] text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Yearly
              <span className="ml-1 text-xs text-green-400">Save 17%</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan)}
                className={`relative bg-[#1a1a2e] border rounded-xl p-5 cursor-pointer transition-all ${
                  selectedPlan?.id === plan.id
                    ? 'border-[#6D4AFF] shadow-[0_0_20px_rgba(109,74,255,0.3)]'
                    : 'border-[#2d2d44] hover:border-[#6D4AFF]/50'
                }`}
              >
                {plan.is_featured && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#6D4AFF] text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" /> POPULAR
                  </div>
                )}
                
                {selectedPlan?.id === plan.id && (
                  <div className="absolute top-3 right-3 w-5 h-5 bg-[#6D4AFF] rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}

                <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                
                <div className="mb-3">
                  <span className="text-2xl font-black text-white">{formatPrice(plan)}</span>
                  <span className="text-gray-500 text-sm">/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                </div>

                <div className="space-y-1.5 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <HardDrive className="w-3.5 h-3.5 text-[#6D4AFF]" />
                    <span>{plan.storage_limit_gb} GB Storage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-[#6D4AFF]" />
                    <span>{plan.emails_per_day} Emails/Day</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FolderPlus className="w-3.5 h-3.5 text-[#6D4AFF]" />
                    <span>{plan.custom_folders_limit} Custom Folders</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Coupon Code Section */}
        <div className="bg-[#1a1a2e] border border-[#2d2d44] rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Ticket className="w-4 h-4 text-[#6D4AFF]" />
            <span className="text-sm font-medium text-gray-300">Have a coupon code?</span>
          </div>
          
          <div className="flex gap-2">
            <Input
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter code"
              className="bg-[#252540] border-[#2d2d44] text-white placeholder:text-gray-600 uppercase"
              disabled={!!appliedCoupon}
            />
            {appliedCoupon ? (
              <Button
                variant="outline"
                onClick={clearCoupon}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10 px-3"
              >
                <X className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleValidateCoupon}
                disabled={!couponCode.trim() || couponValidating}
                className="bg-[#6D4AFF] hover:bg-[#5B3FD9] disabled:opacity-50"
              >
                {couponValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            )}
          </div>
          
          {couponError && (
            <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {couponError}
            </p>
          )}
          
          {appliedCoupon && (
            <div className="mt-2 p-2 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-medium text-sm">{appliedCoupon.code} applied!</span>
                </div>
                <span className="text-green-400 font-bold text-sm">
                  {appliedCoupon.discount_type === 'percentage' 
                    ? `-${appliedCoupon.discount_value}%`
                    : `-$${appliedCoupon.discount_value.toFixed(2)}`
                  }
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={handleCheckout}
            disabled={!selectedPlan || checkoutLoading}
            className="w-full bg-[#6D4AFF] hover:bg-[#5B3FD9] py-6 text-lg font-bold rounded-full shadow-[0_0_20px_rgba(109,74,255,0.3)] hover:shadow-[0_0_30px_rgba(109,74,255,0.5)] transition-all disabled:opacity-50"
          >
            {checkoutLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {selectedPlan ? `Subscribe to ${selectedPlan.name}` : 'Select a Plan'}
              </>
            )}
          </Button>
          
          {!isTrialExpired && (
            <Button
              onClick={onClose}
              variant="ghost"
              className="text-gray-400 hover:text-white hover:bg-[#2d2d44]"
            >
              Continue with trial ({trialDaysLeft} days left)
            </Button>
          )}
          
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="text-gray-500 hover:text-gray-300"
          >
            Sign out
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
